/* eslint-disable */
import React, { useState } from 'react';
import {
  useLocation,
  useNavigate,
  matchPath,
  useHref,
  useLinkClickHandler
} from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button,
  ListItem,
  List,
  Collapse,
  Fade,
  Box,
  Typography,
  ButtonBase
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  ArrowLeft as ArrowBackIcon,
  ArrowRight as ArrowForwardIcon
} from 'react-feather';
import { blueGrey, grey } from '@mui/material/colors';
import NavBarTooltip from 'src/components/widgets/tooltip/NavBarTooltip';
import MenuItemChip from 'src/components/widgets/chip/MenuItemChip';
import { useTheme } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 4
  },
  button: {
    transition:
      'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: theme.spacing(1),
    color: theme.palette.mode === 'light' ? blueGrey[700] : grey[300],
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0,
    padding: '10px 12px 10px 12px',
    textTransform: 'none',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.background.menuItemHover
    },
    '& .MuiButton-label': {
      justifyContent: ({ drawerMode }) => {
        return drawerMode === 'full' ? 'inherit' : 'center';
      }
    }
  },
  button_label: {
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0,
    flexGrow: 1,
    textAlign: 'start'
  },
  icon: {
    marginRight: ({ drawerMode }) => {
      return drawerMode === 'full' ? theme.spacing(1) : 0;
    }
  },
  title: {
    marginRight: 'auto'
  },
  active: {
    color: theme.palette.text.menuItemActive,
    backgroundColor: theme.palette.background.menuItemActive,
    '& $button_label': {
      color: 'inherit',
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: 'inherit'
    }
  },
  nested: {
    paddingLeft: theme.spacing(2)
  }
}));

const NavigateMenuButton = ({
  classes,
  icon,
  drawerMode,
  href,
  forward,
  backward,
  title,
  isNew
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showArrowIcon, setShowArrowIcon] = useState(false);
  const resolveHref = useHref(href);
  const handleForward = () => {
    navigate(href, {
      state: {
        prev: location.pathname
      }
    });
  };

  const handleBackward = () => {
    navigate(location?.state?.prev || '/app/home');
  };

  let menuIcon = null;

  if (backward) {
    menuIcon = (
      <Box
        borderRadius="50%"
        bgcolor={theme.palette.background.paper}
        height={theme.spacing(3)}
        width={theme.spacing(3)}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mr={1}
      >
        <ArrowBackIcon size={20} />
      </Box>
    );
  } else if (icon) {
    const IconComponent = icon;
    menuIcon = <IconComponent className={classes.icon} size="20" />;
  }

  const btn = (
    <ButtonBase
      className={classes.button}
      component="a"
      href={resolveHref}
      onClick={(event) => {
        event.preventDefault();
        if (forward) {
          handleForward();
        } else {
          handleBackward();
        }
      }}
      onMouseEnter={() => setShowArrowIcon(true)}
      onMouseLeave={() => setShowArrowIcon(false)}
    >
      {menuIcon}
      {drawerMode === 'full' && (
        <Box display="inline-flex" flexGrow={1} alignItems="center">
          <Typography variant="body2" className={classes.button_label}>
            {title}
          </Typography>
          {isNew && <MenuItemChip size="small" />}
        </Box>
      )}

      {forward && drawerMode === 'full' && (
        <Fade in={showArrowIcon}>
          <Box
            borderRadius="50%"
            bgcolor={theme.palette.background.paper}
            height={theme.spacing(3)}
            width={theme.spacing(3)}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <ArrowForwardIcon size={20} />
          </Box>
        </Fade>
      )}
    </ButtonBase>
  );

  return drawerMode === 'full' ? (
    btn
  ) : (
    <NavBarTooltip title={title} placement="right">
      {btn}
    </NavBarTooltip>
  );
};

NavigateMenuButton.propTypes = {
  classes: PropTypes.object,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  forward: PropTypes.bool,
  backward: PropTypes.bool,
  title: PropTypes.string,
  isNew: PropTypes.bool,
  drawerMode: PropTypes.string
};

const ChildItemButton = ({ item, classes, drawerMode }) => {
  const location = useLocation();
  const { hash, pathname } = location;
  const resolveHref = useHref(item.href);
  const handleItemClick = useLinkClickHandler(item.href);
  const Icon = item.icon;

  const handleClick = (event) => {
    event.preventDefault();
    if (pathname !== resolveHref) {
      handleItemClick(event);
    }
  };

  const btn = (
    <Button
      className={clsx(classes.button, item.className, {
        [classes.active]: item.hash === hash
      })}
      component="a"
      href={resolveHref}
      onClick={handleClick}
    >
      {Icon && <Icon className={classes.icon} size="20" />}
      {drawerMode === 'full' && (
        <span className={classes.title}>{item.title}</span>
      )}
    </Button>
  );

  return (
    <ListItem
      disableGutters
      onClick={handleClick}
      className={clsx(classes.item, item.className)}
    >
      {drawerMode === 'full' ? (
        btn
      ) : (
        <NavBarTooltip title={item.title} placement="right">
          {btn}
        </NavBarTooltip>
      )}
    </ListItem>
  );
};

ChildItemButton.propTypes = {
  item: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  drawerMode: PropTypes.string
};

const ChildNavigateButton = ({ item, classes, drawerMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const resolveHref = useHref(item.href);
  const Icon = item.icon;
  const handleForward = () => {
    navigate(resolveHref, {
      state: {
        prev: pathname
      }
    });
  };

  const btn = (
    <Button
      onClick={handleForward}
      className={`${classes.button} ${item.className}`}
      key={item.title}
    >
      {Icon && <Icon className={classes.icon} size="20" />}
      {drawerMode === 'full' && (
        <span className={classes.title}>{item.title}</span>
      )}
      {drawerMode === 'full' && <ArrowForwardIcon />}
    </Button>
  );

  return drawerMode === 'full' ? (
    btn
  ) : (
    <NavBarTooltip title={item.title} placement="right">
      {btn}
    </NavBarTooltip>
  );
};

ChildNavigateButton.propTypes = {
  item: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  drawerMode: PropTypes.string
};

const MenuItem = ({
  className,
  href,
  icon,
  title,
  child,
  forward,
  backward,
  isNew,
  drawerMode,
  ...rest
}) => {
  const [open, setOpen] = useState(true);
  const classes = useStyles({ drawerMode });
  const location = useLocation();
  const { pathname } = location;
  const selected = href === pathname;
  const Icon = backward ? ArrowBackIcon : icon;
  const partialMatch =
    href &&
    !!matchPath(
      {
        path: href,
        end: false
      },
      location.pathname
    );
  const exactMath =
    href &&
    !!matchPath(
      {
        path: `${href}/:id`,
        end: true
      },
      location.pathname
    );
  const handleClick = () => {
    if (child && child.length > 0) {
      setOpen(!open);
    }
  };

  const resolveHref = useHref(href);
  const handleItemClick = useLinkClickHandler(href);

  const getButton = () => {
    const btn = (
      <ButtonBase
        className={clsx(classes.button, {
          [classes.active]: selected || exactMath || partialMatch
        })}
        component="a"
        href={resolveHref}
        onClick={(event) => {
          event.preventDefault();
          if (pathname !== resolveHref) {
            handleItemClick(event);
          }
        }}
      >
        {Icon && <Icon className={classes.icon} size="20" />}
        {drawerMode === 'full' && (
          <Box display="inline-flex" flexGrow={1} alignItems="center">
            <Typography
              component="span"
              variant="body2"
              className={classes.button_label}
            >
              {title}
            </Typography>
            {isNew && <MenuItemChip size="small" />}
          </Box>
        )}
        {child && child.length > 0 ? (
          <>{open ? <ExpandLess /> : <ExpandMore />}</>
        ) : (
          ''
        )}
      </ButtonBase>
    );

    return drawerMode === 'full' ? (
      btn
    ) : (
      <NavBarTooltip title={title} placement="right">
        {btn}
      </NavBarTooltip>
    );
  };

  return (
    <>
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        {...rest}
        onClick={handleClick}
      >
        {forward || backward ? (
          <NavigateMenuButton
            classes={classes}
            icon={icon}
            drawerMode={drawerMode}
            href={href}
            forward={forward}
            backward={backward}
            title={title}
            isNew={isNew}
          />
        ) : (
          getButton()
        )}
      </ListItem>
      {child && child.length > 0 ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding className={classes.nested}>
            {child.map(({ forward: subForward, ...childItem }) => {
              return subForward ? (
                <ChildNavigateButton
                  item={childItem}
                  key={childItem.title}
                  classes={classes}
                  drawerMode={drawerMode}
                />
              ) : (
                <ChildItemButton
                  key={childItem.title}
                  item={childItem}
                  classes={classes}
                  drawerMode={drawerMode}
                />
              );
            })}
          </List>
        </Collapse>
      ) : (
        ''
      )}
    </>
  );
};

MenuItem.propTypes = {
  className: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  forward: PropTypes.bool,
  backward: PropTypes.bool,
  title: PropTypes.string,
  child: PropTypes.array,
  isNew: PropTypes.bool,
  drawerMode: PropTypes.string
};

export default MenuItem;
